import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, Button, Typography, Box,
    Checkbox, CircularProgress, IconButton,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';

/**
 * "Publish Workspace" (header-level, network only) — SNW-ALL-My Schools-Publish.
 *
 * NOT the same flow as the existing per-row "Publish Workspace" button
 * (handleOpenPublish / ConfirmPublishDialog /
 * PublishWorkspaceSuccessDialog in DQProClientList.page.jsx and
 * PublishWorkspaceDialogs.comp.jsx) — that publishes ONE school a network
 * picks from its row. This is the network EXPLICITLY choosing any number of
 * its still-unpublished schools at once, from a single header button.
 *
 * Reuses the same underlying publishClientService the per-row flow already
 * calls, once per selected school — real backend calls, not a mock. No consent
 * pathway travels with a publish: each school sets up its own from its own
 * workspace, with the network's method pre-selected for it (SNW, SW Parental
 * Consent Flow, 5 Oct 2026).
 *
 * Three steps: select -> confirm -> published, worded as Ian revised them
 * (bug deck LA-SNW-07-Oct-3, slides 41-43, 9 Oct 2026). The published step
 * replaces what follows below for any number of schools.
 *
 * (Earlier note, kept for history:) Text/labels/counts on both are taken
 * directly from the approved mockup. There is deliberately NO third "success"
 * step in this component — the mockup's success screen only shows the
 * single-school case, and per explicit instruction nothing is invented for
 * the N>1 case it doesn't cover. Instead this hands its outcome back to the
 * caller via onPublished({ succeeded, failed }): when exactly one school was
 * published, the caller can show the real, already-built single-school
 * PublishWorkspaceSuccessDialog (byte-for-byte the approved mockup); for
 * everything else it falls back to a plain toast until an N>1 success mockup
 * exists.
 */

const ORANGE = 'var(--brand-primary, #FF6900)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const BORDER = 'var(--brand-border, #E5E7EB)';

const primaryBtnSx = {
    textTransform: 'none', fontWeight: 700, fontSize: '13.5px',
    backgroundColor: ORANGE, color: '#fff',
    borderRadius: '50px', px: 2.5, py: 0.9, boxShadow: 'none',
    '&:hover': { backgroundColor: '#e55a00' },
    '&.Mui-disabled': { backgroundColor: ORANGE, color: '#fff', opacity: 0.5 },
};
const outlineBtnSx = {
    textTransform: 'none', fontWeight: 700, fontSize: '13.5px',
    color: MUTED, borderRadius: '50px', px: 2.5, py: 0.9,
    border: `1.5px solid ${ORANGE}`,
    '&:hover': { borderColor: '#e55a00', color: ORANGE, backgroundColor: 'rgba(255,105,0,0.04)' },
};

const NOTE = 'Once published, each school’s contact will be able to access their school’s workspace.';

const BulkPublishSchoolsDialog = ({ open, onClose, schools = [], onPublished, publishOne, onResend }) => {
    const [step, setStep] = useState('select');
    const [selected, setSelected] = useState([]);
    const [publishing, setPublishing] = useState(false);
    // What the Workspaces Published step reports.
    const [result, setResult] = useState(null);
    const [resending, setResending] = useState(false);

    /*
     * What names ONE school here.
     *
     * Schools of a network share that network's org_code, so org_code alone
     * is the same string on every sibling — ticking one would tick them all,
     * and "select all" would build a list of duplicates. A standalone school
     * has no institute and falls back to its code, which is already unique.
     */
    const idOf = (s) => [s.org_code, s.institute_code].filter(Boolean).join('::');

    const eligible = schools.filter((s) => !s.isPublished);

    useEffect(() => {
        if (open) {
            setStep('select');
            setSelected([]);
            setResult(null);
        }
    }, [open]);

    const toggle = (id) => {
        setSelected((sel) => (sel.includes(id) ? sel.filter((c) => c !== id) : [...sel, id]));
    };

    const toggleAll = () => {
        setSelected((sel) => (sel.length === eligible.length ? [] : eligible.map(idOf)));
    };

    const handleClose = () => {
        if (publishing) return;
        onClose();
    };

    const handlePublish = async () => {
        setPublishing(true);
        const targets = eligible.filter((s) => selected.includes(idOf(s)));
        // institute_code travels with org_code: schools of a network share the
        // code, and only the institute says which one is being published.
        const outcomes = await Promise.allSettled(targets.map((s) => publishOne(s.org_code, s.institute_code)));
        const succeeded = [];
        const failed = [];
        outcomes.forEach((outcome, i) => {
            const school = targets[i];
            if (outcome.status === 'fulfilled' && outcome.value?.success) {
                succeeded.push({
                    ...school,
                    contact_email: outcome.value.contact_email || school.contact_email,
                    published_at: outcome.value.published_at,
                    credentials_emailed: !!outcome.value.credentials_emailed,
                });
            } else {
                failed.push(school);
            }
        });
        setPublishing(false);
        if (onPublished) onPublished({ succeeded, failed });
        if (succeeded.length === 0) {
            // Nothing went live — the caller reports the failures.
            setStep('select');
            onClose();
            return;
        }
        setResult({ succeeded, failed, at: succeeded[0].published_at || new Date().toISOString() });
        setStep('done');
    };

    const chosen = eligible.filter((sc) => selected.includes(idOf(sc)));

    const stamp = (() => {
        const d = result?.at ? new Date(result.at) : null;
        if (!d || Number.isNaN(d.getTime())) return null;
        const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        return `Published on ${date} · ${time}`;
    })();

    const handleResendAll = async () => {
        if (!onResend || resending) return;
        setResending(true);
        for (const sc of result?.succeeded || []) {
            // One at a time: each call reports its own outcome.
            // eslint-disable-next-line no-await-in-loop
            await Promise.resolve(onResend(sc.org_code, sc.institute_code)).catch(() => {});
        }
        setResending(false);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '20px', backgroundColor: 'var(--brand-bg, #FFFBF7)' } }}
        >
            <DialogContent sx={{ p: 3, position: 'relative' }}>
                <IconButton
                    onClick={handleClose}
                    aria-label="Close"
                    size="small"
                    disabled={publishing}
                    sx={{ position: 'absolute', top: 12, right: 12, color: MUTED }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                {step === 'select' && (
                    <>
                        <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--brand-text, #111827)', mb: 0.5 }}>
                            Which schools get a workspace?
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: MUTED, mb: 2.5 }}>
                            Select the schools in your network to publish their workspaces. Schools with published
                            workspaces cannot be selected again.
                        </Typography>

                        <Box
                            onClick={toggleAll}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                                border: `1px solid ${BORDER}`, borderRadius: '10px', p: '12px 14px', mb: 2,
                                backgroundColor: 'var(--brand-surface, #fff)',
                            }}
                        >
                            <Checkbox
                                checked={eligible.length > 0 && selected.length === eligible.length}
                                indeterminate={selected.length > 0 && selected.length < eligible.length}
                                disabled={eligible.length === 0}
                                sx={{ p: 0.5, color: BORDER, '&.Mui-checked': { color: ORANGE }, '&.MuiCheckbox-indeterminate': { color: ORANGE } }}
                            />
                            <Box>
                                <Typography sx={{ fontSize: '13.5px', fontWeight: 700 }}>Every school in the network</Typography>
                                <Typography sx={{ fontSize: '12px', color: MUTED }}>Selects all schools listed below.</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
                            {/* SCHOOL · CONTACT EMAIL · STATUS */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: '8px 14px', borderBottom: `1px solid ${BORDER}`, backgroundColor: 'var(--brand-bg, #FAF7F5)' }}>
                                <Box sx={{ width: 28, flexShrink: 0 }} />
                                <Typography sx={{ flex: 1, minWidth: 0, fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.5px', color: MUTED }}>SCHOOL</Typography>
                                <Typography sx={{ flex: 1, minWidth: 0, fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.5px', color: MUTED }}>CONTACT EMAIL</Typography>
                                <Typography sx={{ width: 104, flexShrink: 0, textAlign: 'right', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.5px', color: MUTED }}>STATUS</Typography>
                            </Box>
                            {schools.map((s, i) => (
                                <Box
                                    key={idOf(s)}
                                    onClick={() => !s.isPublished && toggle(idOf(s))}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.25, p: '12px 14px',
                                        cursor: s.isPublished ? 'default' : 'pointer',
                                        borderBottom: i < schools.length - 1 ? `1px solid ${BORDER}` : 'none',
                                        backgroundColor: 'var(--brand-surface, #fff)',
                                    }}
                                >
                                    <Checkbox
                                        checked={s.isPublished || selected.includes(idOf(s))}
                                        disabled={s.isPublished}
                                        sx={{ p: 0.5, color: BORDER, '&.Mui-checked': { color: ORANGE } }}
                                    />
                                    {/* The enrolled / classes line is gone; the contact email takes its place. */}
                                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: '13.5px', fontWeight: 700 }}>{s.org_name}</Typography>
                                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: '12.5px', color: MUTED, overflowWrap: 'anywhere' }}>{s.contact_email || '—'}</Typography>
                                    <Typography sx={{
                                        width: 104, flexShrink: 0, textAlign: 'right',
                                        fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase',
                                        color: s.isPublished ? MUTED : '#1B6B55', whiteSpace: 'nowrap',
                                    }}>
                                        {s.isPublished ? 'Published' : 'Not published'}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                            border: `1px solid ${BORDER}`, borderRadius: '10px', p: '12px 14px', mt: 2,
                            backgroundColor: 'var(--brand-surface, #fff)',
                        }}>
                            <Typography sx={{ fontSize: '12.5px', color: MUTED }}>
                                {NOTE}
                            </Typography>
                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1B6B55', whiteSpace: 'nowrap' }}>
                                {selected.length} of {eligible.length} schools selected
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2.5 }}>
                            <Button variant="outlined" sx={outlineBtnSx} onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button
                                sx={primaryBtnSx}
                                disabled={selected.length === 0}
                                onClick={() => setStep('confirm')}
                            >
                                Publish workspace
                            </Button>
                        </Box>
                    </>
                )}

                {step === 'confirm' && (
                    <>
                        <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--brand-text, #111827)', mb: 0.5 }}>
                            Publish school workspaces?
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: MUTED, mb: 2.5 }}>
                            You are about to publish workspaces for {selected.length} school{selected.length === 1 ? '' : 's'} in the network.
                        </Typography>

                        {/* The schools chosen and where each activation email goes (slide 42). */}
                        <Typography sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.6px', color: MUTED, mb: 0.75 }}>SELECTED SCHOOLS</Typography>
                        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden', backgroundColor: 'var(--brand-surface, #fff)', maxHeight: 260, overflowY: 'auto' }}>
                            {chosen.map((sc, i) => (
                                // The school name is the bold one, its email the quiet one (slide 42).
                                <Box key={idOf(sc)} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: '12px 14px', borderBottom: i < chosen.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                                    <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--brand-text, #111827)' }}>{sc.org_name}</Typography>
                                    <Typography sx={{ fontSize: '13px', color: MUTED, overflowWrap: 'anywhere', textAlign: 'right' }}>{sc.contact_email || '—'}</Typography>
                                </Box>
                            ))}
                        </Box>

                        <Typography sx={{ fontSize: '12.5px', color: MUTED, mt: 2 }}>
                            {NOTE}
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2.5 }}>
                            <Button variant="outlined" sx={outlineBtnSx} disabled={publishing} onClick={() => setStep('select')}>
                                Cancel
                            </Button>
                            <Button
                                sx={primaryBtnSx}
                                disabled={publishing}
                                startIcon={publishing ? <CircularProgress size={15} color="inherit" /> : null}
                                onClick={handlePublish}
                            >
                                {publishing ? 'Publishing…' : 'Confirm'}
                            </Button>
                        </Box>
                    </>
                )}
                {step === 'done' && result && (() => {
                    const n = result.succeeded.length;
                    return (
                        <Box sx={{ textAlign: 'center', pt: 1 }}>
                            <Box sx={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: 'rgba(46, 125, 50, 0.12)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CheckIcon style={{ fontSize: 28, color: '#2e7d32' }} />
                            </Box>
                            <Typography sx={{ fontSize: '21px', fontWeight: 800, mt: 2 }}>
                                {n === 1 ? 'Workspace Published' : 'Workspaces Published'}
                            </Typography>
                            <Typography sx={{ fontSize: '14px', mt: 0.75 }}>
                                <b>{n} school workspace{n === 1 ? '' : 's'}</b> {n === 1 ? 'is' : 'are'} ready to go.
                            </Typography>
                            <Typography sx={{ fontSize: '13px', color: MUTED, mt: 0.75 }}>
                                School contacts can now sign in and access the products, assessments, and programs you assign.
                            </Typography>

                            <Box sx={{ textAlign: 'left', border: `1px solid ${BORDER}`, borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', p: '14px 16px', mt: 2.5 }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pb: 1.25, borderBottom: `1px solid ${BORDER}` }}>
                                    <CheckIcon style={{ fontSize: 16, color: '#2e7d32' }} />
                                    <Typography sx={{ fontSize: '13.5px' }}>Workspaces are active for {n} school{n === 1 ? '' : 's'}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pt: 1.25 }}>
                                    <CheckIcon style={{ fontSize: 16, color: '#2e7d32' }} />
                                    <Typography sx={{ fontSize: '13.5px' }}>Activation emails sent to the school contacts</Typography>
                                </Box>
                                <Box sx={{ pl: 3, pt: 0.75, maxHeight: 150, overflowY: 'auto' }}>
                                    {result.succeeded.map((sc) => (
                                        <Typography key={idOf(sc)} sx={{ fontSize: '12.5px', fontFamily: 'monospace', color: 'var(--brand-text, #374151)', overflowWrap: 'anywhere' }}>
                                            {sc.contact_email || sc.org_name}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>

                            {result.failed.length > 0 && (
                                <Typography sx={{ fontSize: '12.5px', color: '#B45309', mt: 1.5 }}>
                                    {result.failed.length} school{result.failed.length === 1 ? '' : 's'} could not be published: {result.failed.map((f) => f.org_name).join(', ')}.
                                </Typography>
                            )}
                            {stamp && <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', color: MUTED, mt: 2 }}>{stamp}</Typography>}

                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 2.5, flexWrap: 'wrap' }}>
                                <Button sx={primaryBtnSx} onClick={onClose}>Back to My Schools</Button>
                                {onResend && (
                                    <Button variant="outlined" sx={outlineBtnSx} disabled={resending} onClick={handleResendAll}>
                                        {resending ? 'Resending…' : (n === 1 ? 'Resend activation email' : 'Resend activation emails')}
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    );
                })()}
            </DialogContent>
        </Dialog>
    );
};

export default BulkPublishSchoolsDialog;
